import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoPlay } from 'react-icons/io5';
import { LuChevronRight, LuExternalLink, LuPanelRight, LuPlus, LuSparkles } from 'react-icons/lu';
import { NodeFormProvider } from '../Components/NodeFormKit';
import { isDarkMode, SCROLL_CLASS, SCROLL_CSS } from '../Others/BotCard';
import { CONFIG_CSS } from '../Others/BotConfigKit';
import { TOOL_TYPES } from './agentCatalog';
import { AGENT_FIELDS_CSS } from './AgentFields';
import { InstructionsTab, SettingsTab, ValidationBox } from './AgentPanel';
import AgentTester from './AgentTester';
import ToolEditor from './ToolEditor';

const FORM_THEMES = {
  dark: { cardBg: '#111b21', border: '#2a3942', borderStrong: '#4e606b', textPrimary: '#e9edef', textSecondary: '#aebac1', textTertiary: '#9fb4b9', panelBg: '#111b21', inputBg: '#202c33', accent: '#5DCCED', selected: '#153543', headerBg: '#111b21', chatBg: '#0b141a' },
  light: { cardBg: '#ffffff', border: '#e3e9ec', borderStrong: '#C3C3C3', textPrimary: '#2D2D2D', textSecondary: '#555555', textTertiary: '#747474', panelBg: '#ffffff', inputBg: '#f4fafc', accent: '#0a7ba5', selected: '#dff3fa', headerBg: '#ffffff', chatBg: '#e5ddd5' },
};

const WORKSPACE_CSS = `
.su-ag { position: absolute; inset: 0; z-index: 900; display: flex; background: var(--su-surface); overflow: hidden; }
.su-ag-left { flex: 1 1 50%; min-width: 0; overflow-y: auto; display: flex; justify-content: center; align-items: flex-start; padding: 48px 24px 32px; background-color: var(--su-bg); background-image: radial-gradient(var(--su-ag-dot) 1px, transparent 1px); background-size: 20px 20px; }
.su-kit { --su-ag-dot: #cfe3ec; }
#body-dark.dark .su-kit { --su-ag-dot: #222e35; }
.su-ag-node { position: relative; width: 100%; max-width: 460px; }
.su-ag-start { position: absolute; top: -11px; left: 50%; transform: translateX(-50%); z-index: 1; display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 6px; background: #7563D8; color: #ffffff; font-size: 10px; font-weight: 600; letter-spacing: 0.05em; }
.su-ag-card { overflow: hidden; border: 1px solid var(--su-line); border-radius: 16px; background: var(--su-surface); }
.su-ag-head { display: flex; align-items: center; gap: 14px; width: 100%; padding: 20px 18px 18px; border: none; border-bottom: 1px solid var(--su-line-2); background: none; color: var(--su-ink); text-align: left; cursor: pointer; }
.su-ag-head:hover { background: var(--su-hover); }
.su-ag-head.on { background: var(--su-chip-on); }
.su-ag-big { width: 44px; height: 44px; flex-shrink: 0; display: grid; place-items: center; border-radius: 12px; background: #7563D8; color: #ffffff; }
.su-ag-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.su-ag-txt b { font-size: 18px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.su-ag-txt small { font-size: 12.5px; color: var(--su-ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.su-ag-txt em { font-style: normal; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--su-ink-3); }
.su-ag-txt em.dirty { color: #a76f0c; }
.su-ag-tools { display: flex; flex-direction: column; gap: 2px; padding: 10px; }
.su-ag-cap { padding: 4px 8px 6px; font-size: 11.5px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--su-ink-3); }
.su-ag-none { padding: 6px 8px 10px; font-size: 13px; color: var(--su-ink-3); }
.su-ag-tool { display: flex; align-items: center; gap: 12px; width: 100%; padding: 9px 8px; border: 1px solid transparent; border-radius: 10px; background: none; color: var(--su-ink); text-align: left; cursor: pointer; }
.su-ag-tool:hover { background: var(--su-hover); }
.su-ag-tool.on { border-color: var(--su-accent-ink); background: var(--su-chip-on); }
.su-ag-tool.live { border-color: #7563D8; }
.su-ag-ic { width: 32px; height: 32px; flex-shrink: 0; display: grid; place-items: center; border-radius: 50%; color: #ffffff; font-size: 15px; }
.su-ag-tool .su-ag-txt b { font-size: 14.5px; }
.su-ag-chev { color: var(--su-ink-3); flex-shrink: 0; }
.su-ag-used { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--su-wa-ink); white-space: nowrap; }
.su-ag-used i { width: 6px; height: 6px; border-radius: 50%; background: var(--su-wa); }
.su-ag-addtool { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 6px; padding: 10px; border: 1px dashed var(--su-control); border-radius: 10px; background: none; color: var(--su-ink-2); font-size: 13px; font-weight: 600; cursor: pointer; }
.su-ag-addtool:hover { border-color: var(--su-accent-ink); color: var(--su-accent-ink); }
.su-ag-right { flex: 1 1 50%; min-width: 420px; display: flex; flex-direction: column; border-left: 1px solid var(--su-line); background: var(--su-surface); }
.su-ag-ph { display: flex; align-items: center; gap: 12px; min-height: 64px; padding: 8px 16px; border-bottom: 1px solid var(--su-line-2); flex-shrink: 0; }
.su-ag-ph .su-ag-txt b { font-size: 19px; }
.su-ag-crumb { padding: 0; border: none; background: none; color: var(--su-accent-ink); font-size: 12.5px; font-weight: 600; cursor: pointer; }
.su-ag-tabs { display: flex; gap: 18px; padding: 0 16px; border-bottom: 1px solid var(--su-line); flex-shrink: 0; overflow-x: auto; overflow-y: hidden; scrollbar-width: none; }
.su-ag-tabs::-webkit-scrollbar { display: none; }
.su-ag-tabs button { padding: 12px 0 10px; border: none; border-bottom: 2.5px solid transparent; background: none; color: var(--su-ink-4); font-size: 14px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.su-ag-tabs button:hover { color: var(--su-ink-2); }
.su-ag-tabs button.on { color: var(--su-ink); border-bottom-color: var(--su-sky); }
.su-ag-body { flex: 1; min-height: 0; overflow-y: auto; padding: 16px; }
.su-ag-body.flush { padding: 0; }
.su-ag-body.flush > .su-ag-valid { padding: 14px 16px 0; }
.su-ag-foot { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--su-line); flex-shrink: 0; }
.su-ag-foot.dirty { background: var(--su-note-bg); border-top-color: var(--su-note-line); }
.su-ag-foot span { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--su-ink); }
.su-ag-step { display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid var(--su-line); border-radius: 12px; background: var(--su-bg); }
.su-ag-num { width: 24px; height: 24px; flex-shrink: 0; display: grid; place-items: center; margin-top: 8px; border-radius: 50%; background: var(--su-accent); color: #ffffff; font-size: 12px; font-weight: 600; }
.su-ag-acc .su-acc-t small { max-width: 100%; }
.su-kit { --su-ag-back: #edf6fb; --su-ag-dot: #d4dde0; }
#body-dark.dark .su-kit { --su-ag-back: #0f171c; --su-ag-dot: #26343c; }
.su-ag-left { background-color: var(--su-ag-back); background-size: 22px 22px; }
.su-ag-node { --su-surface: #ffffff; --su-ink: #1f2937; --su-ink-3: #6b7280; --su-line-2: #e5e7eb; --su-chip-on: #f3f4f6; --su-hover: #f3f4f6; --su-accent-ink: #7563D8; --su-control: #d1d5db; }
#body-dark.dark .su-kit .su-ag-node { --su-surface: #1b222b; --su-ink: #f2f5f8; --su-ink-3: #8a96a5; --su-line-2: #2a323c; --su-chip-on: #232b36; --su-hover: #232b36; --su-accent-ink: #7563D8; --su-control: #2a323c; }
.su-ag-card { border: 1px solid #7563D8; border-radius: 14px; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22); }
.su-ag-node .su-ag-txt b { font-weight: 700; }
.su-ag-node .su-ag-txt em, .su-ag-node .su-ag-cap { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; }
.su-ag-node .su-ag-tool .su-ag-txt b { font-weight: 600; font-size: 14px; }
.su-ag-right { position: relative; flex: none; min-width: 420px; max-width: calc(100% - 320px); }
.su-ag-right.float { position: fixed; z-index: 5; max-width: calc(100vw - 80px); border: 1px solid var(--su-line); border-radius: 14px; box-shadow: 0 16px 44px rgba(0, 0, 0, 0.3); overflow: hidden; transition: none; }
.su-ag-right.float .su-ag-ph, .su-ag-right.float .su-wa-h { cursor: move; }
.su-ag-rz { position: absolute; top: 0; bottom: 0; left: 0; width: 6px; z-index: 6; cursor: ew-resize; }
.su-ag-right.float .su-ag-rz { left: auto; right: 0; }
.su-ag-rz:hover, .su-ag-rz.on { background: var(--su-sky); }
.su-ag-dock { width: 34px; height: 34px; flex-shrink: 0; display: grid; place-items: center; border: none; border-radius: 9px; background: none; color: var(--su-ink-2); cursor: pointer; }
.su-ag-dock:hover { background: var(--su-raised); }
.su-ag-ph { background: var(--su-surface); }
.su-ag-tabs { background: var(--su-surface); }
.su-ag-tabs button.on { color: var(--su-accent-ink); }
.su-ag-body, .su-ag-body.flush { padding: 14px; background: var(--su-bg); }
.su-ag-body.flush > .su-ag-valid { padding: 0; }
.su-ag-acc { display: flex; flex-direction: column; gap: 10px; }
.su-ag-acc .su-acc { overflow: hidden; border: 1px solid var(--su-line); border-radius: 14px; background: var(--su-surface); }
.su-ag-acc .su-acc.open { border-color: var(--c); }
.su-ag-acc .su-acc.open .su-acc-h { margin-bottom: 12px; background: color-mix(in srgb, var(--c) 15%, var(--su-surface)); }
.su-ag-right .su-af-section { margin-bottom: 10px; padding: 14px; border: 1px solid var(--su-line); border-radius: 14px; background: var(--su-surface); }
.su-ag-right .su-af-section:last-child { margin-bottom: 0; padding-bottom: 14px; }
.su-ag-right .su-af-section:first-child { padding-top: 14px; }
.su-ag .su-soft-scroll { scrollbar-color: auto; }
.su-ag .su-soft-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
.su-ag .su-soft-scroll::-webkit-scrollbar-thumb { background-color: transparent; }
.su-ag .su-soft-scroll:hover::-webkit-scrollbar-thumb { background-color: var(--su-control); }
`;

const LAYOUT_KEY = 'su.agentEditorLayout';
const MIN_WIDTH = 420;

const readLayout = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(LAYOUT_KEY)) || {};
    return { floating: Boolean(saved.floating), width: Math.max(MIN_WIDTH, Number(saved.width) || Math.round(window.innerWidth * 0.48)), x: Number(saved.x) || 0, y: Number(saved.y) || 0 };
  } catch (error) {
    return { floating: false, width: Math.max(MIN_WIDTH, Math.round(window.innerWidth * 0.48)), x: 0, y: 0 };
  }
};

const AGENT_TABS = [
  { key: 'instructions', label: 'Instrucciones' },
  { key: 'inputs', label: 'Contexto y entradas' },
  { key: 'tracking', label: 'Seguimiento' },
  { key: 'settings', label: 'Ajustes' },
];

const AgentWorkspace = ({
  config,
  onChange,
  selection,
  setSelection,
  onSave,
  saving,
  dirty,
  validation,
  flows,
  nodes = [],
  listAreas,
  listAdvisers,
  listTags,
  listStages,
  listLists,
  isDark,
  onAddTool,
  botId,
  api,
  onTurn,
  liveTools = [],
  testing,
  onStopTest,
}) => {
  const formTheme = isDarkMode(isDark) ? FORM_THEMES.dark : FORM_THEMES.light;
  const [usedTools, setUsedTools] = useState([]);
  const [layout, setLayout] = useState(readLayout);
  const [resizing, setResizing] = useState(false);
  const asideRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
    } catch (error) {
      console.log('No se pudo guardar el diseño del editor:', error);
    }
  }, [layout]);

  const areaTop = () => asideRef.current?.parentElement?.getBoundingClientRect().top || 0;

  const toggleDock = () => {
    setLayout((current) =>
      current.floating
        ? { ...current, floating: false }
        : { ...current, floating: true, x: Math.max(20, window.innerWidth - current.width - 24), y: areaTop() + 16 }
    );
  };

  const startResize = (event) => {
    event.preventDefault();
    const aside = asideRef.current;
    const startX = event.clientX;
    const startWidth = aside.getBoundingClientRect().width;
    let next = startWidth;
    setResizing(true);
    const move = (moveEvent) => {
      const delta = layout.floating ? moveEvent.clientX - startX : startX - moveEvent.clientX;
      next = Math.max(MIN_WIDTH, startWidth + delta);
      aside.style.width = `${next}px`;
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      setResizing(false);
      setLayout((current) => ({ ...current, width: Math.round(aside.getBoundingClientRect().width) }));
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const startDrag = (event) => {
    if (!layout.floating || event.target.closest('button') || !event.target.closest('.su-ag-ph, .su-wa-h')) return;
    event.preventDefault();
    const aside = asideRef.current;
    const box = aside.getBoundingClientRect();
    const top = areaTop() + 8;
    let x = box.left;
    let y = box.top;
    const offsetX = event.clientX - box.left;
    const offsetY = event.clientY - box.top;
    const move = (moveEvent) => {
      x = Math.max(0, Math.min(window.innerWidth - box.width, moveEvent.clientX - offsetX));
      y = Math.max(top, Math.min(window.innerHeight - 120, moveEvent.clientY - offsetY));
      aside.style.transform = `translate3d(${x - box.left}px, ${y - box.top}px, 0)`;
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      aside.style.transform = '';
      setLayout((current) => ({ ...current, x, y }));
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  const dockButton = (
    <button type="button" className="su-ag-dock" title={layout.floating ? 'Fijar a la derecha' : 'Soltar y mover'} aria-label={layout.floating ? 'Fijar a la derecha' : 'Soltar y mover'} onClick={toggleDock}>
      {layout.floating ? <LuPanelRight className="su-ic" size={17} /> : <LuExternalLink className="su-ic" size={16} />}
    </button>
  );
  const formContext = useMemo(() => ({ theme: formTheme, color: formTheme.accent }), [formTheme]);
  const tools = config.tools || [];
  const openIndex = selection?.view === 'tool' && tools[selection.toolIndex] ? selection.toolIndex : null;
  const tool = openIndex !== null ? tools[openIndex] : null;
  const tab = AGENT_TABS.some((item) => item.key === selection?.tab) ? selection.tab : 'instructions';
  const set = (patch) => onChange({ ...config, ...patch });
  const setInstructions = (patch) => set({ instructions: { ...(config.instructions || {}), ...patch } });
  const memoryHours = Math.round(((config.memoryTtlMin || 120) / 60) * 10) / 10;

  const openAgentTab = (key) => {
    onStopTest?.();
    setSelection({ view: 'agent', tab: key });
  };
  const openTool = (index) => {
    onStopTest?.();
    setSelection({ view: 'tool', toolIndex: index });
  };

  const removeTool = (index) => {
    const removed = tools[index];
    const steps = (config.instructions?.steps || []).map((step) => (step.tool === removed?.name ? { ...step, tool: undefined } : step));
    onChange({ ...config, tools: tools.filter((_, i) => i !== index), instructions: { ...(config.instructions || {}), steps } });
    setSelection({ view: 'agent', tab: 'instructions' });
  };

  const problemsOf = (item) => {
    const label = item.label || item.name;
    const mine = (list) => (list || []).filter((text) => text.includes(label) || text.includes(item.name));
    return { errors: mine(validation?.errors), warnings: mine(validation?.warnings) };
  };

  const toolStatus = (item) => {
    if (problemsOf(item).errors.length) return <span className="su-tag" style={{ '--c': '#FF2242' }}>Revisar</span>;
    if (liveTools.includes(item.name)) return <span className="su-tag ai">En uso</span>;
    if (usedTools.includes(item.name)) {
      return (
        <span className="su-ag-used">
          <i />
          Usada
        </span>
      );
    }
    if (item.suggested) return <span className="su-tag">Sugerida</span>;
    return <LuChevronRight className="su-ic su-ag-chev" size={16} />;
  };

  const toolRow = (item, index) => {
    const meta = TOOL_TYPES[item.type] || TOOL_TYPES.node;
    const Icon = meta.icon;
    const selected = !testing && openIndex === index;
    return (
      <button
        key={`${item.name}-${index}`}
        type="button"
        aria-pressed={selected}
        className={`su-ag-tool${selected ? ' on' : ''}${liveTools.includes(item.name) ? ' live' : ''}`}
        onClick={() => openTool(index)}
      >
        <span className="su-ag-ic" style={{ backgroundColor: meta.color }}>
          <Icon />
        </span>
        <span className="su-ag-txt">
          <b>{item.label || item.name}</b>
          <small>{String(item.whenToUse || '').trim() || 'Define cuándo usarla'}</small>
        </span>
        {toolStatus(item)}
      </button>
    );
  };

  const panelHeader = (icon, color, caption, title, extra) => (
    <div className="su-ag-ph">
      <span className="su-ag-ic" style={{ backgroundColor: color }}>
        {React.createElement(icon)}
      </span>
      <span className="su-ag-txt">
        <small>{caption}</small>
        <b>{title}</b>
      </span>
      {extra}
    </div>
  );

  const footer = (children) =>
    dirty || children ? (
      <div className={`su-ag-foot${dirty ? ' dirty' : ''}`}>
        {dirty && <span>Cambios sin guardar</span>}
        {children}
        {dirty && (
          <button type="button" className="su-btn" onClick={onSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        )}
      </div>
    ) : null;

  const agentTitle = config.companyName ? `Asistente de ${config.companyName}` : 'Asistente';
  const toolMeta = tool ? TOOL_TYPES[tool.type] || TOOL_TYPES.node : null;

  return (
    <NodeFormProvider value={formContext}>
      <style>{SCROLL_CSS}</style>
      <style>{CONFIG_CSS}</style>
      <style>{AGENT_FIELDS_CSS}</style>
      <style>{WORKSPACE_CSS}</style>
      <div className="su-ag">
        <div className={`su-ag-left ${SCROLL_CLASS}`}>
          <div className="su-ag-node">
            <span className="su-ag-start">
              <IoPlay size={9} />
              INICIO
            </span>
            <div className="su-ag-card">
              <button type="button" aria-pressed={!testing && !tool} className={`su-ag-head${!testing && !tool ? ' on' : ''}`} onClick={() => openAgentTab(tab)}>
                <span className="su-ag-big">
                  <LuSparkles className="su-ic" size={22} />
                </span>
                <span className="su-ag-txt">
                  <em className={dirty ? 'dirty' : ''}>Agente IA{dirty ? ' · sin guardar' : ''}</em>
                  <b>{agentTitle}</b>
                  <small>
                    {config.model || 'gpt-4.1-mini'} · memoria {memoryHours} h
                  </small>
                </span>
                <LuChevronRight className="su-ic su-ag-chev" size={16} />
              </button>
              <div className="su-ag-tools">
                <span className="su-ag-cap">Herramientas · {tools.length}</span>
                {!tools.length && <span className="su-ag-none">Todavía no tiene herramientas: solo puede conversar.</span>}
                {tools.map(toolRow)}
                <button type="button" className="su-ag-addtool" onClick={() => onAddTool()}>
                  <LuPlus className="su-ic" size={14} />
                  Agregar herramienta
                </button>
              </div>
            </div>
          </div>
        </div>

        <aside
          ref={asideRef}
          className={`su-ag-right${layout.floating ? ' float' : ''}`}
          aria-label={testing ? 'Probar en vivo' : 'Configuración del agente'}
          onMouseDown={startDrag}
          style={
            layout.floating
              ? { width: `${layout.width}px`, left: `${layout.x}px`, top: `${layout.y}px`, height: `min(720px, calc(100vh - ${layout.y + 16}px))` }
              : { width: `${layout.width}px` }
          }
        >
          <span className={`su-ag-rz${resizing ? ' on' : ''}`} role="separator" aria-orientation="vertical" aria-label="Cambiar el ancho del editor" onPointerDown={startResize} />
          {testing ? (
            <AgentTester botId={botId} api={api} config={config} onUsedTools={setUsedTools} onTurn={onTurn} headerExtra={dockButton} />
          ) : tool ? (
            <>
              {panelHeader(
                toolMeta.icon,
                toolMeta.color,
                <>
                  <button type="button" className="su-ag-crumb" onClick={() => openAgentTab(tab)}>
                    Agente
                  </button>
                  {' › Herramienta'}
                </>,
                tool.label || tool.name,
                <>
                  <button type="button" className="su-btn danger" onClick={() => removeTool(openIndex)}>
                    Quitar
                  </button>
                  {dockButton}
                </>
              )}
              <div className={`su-ag-body ${SCROLL_CLASS}`}>
                <ValidationBox validation={problemsOf(tool)} />
                <ToolEditor
                  tool={tool}
                  index={openIndex}
                  tools={tools}
                  nodes={nodes}
                  flows={flows}
                  listAreas={listAreas}
                  listAdvisers={listAdvisers}
                  listTags={listTags}
                  listStages={listStages}
                  listLists={listLists}
                  hasTracking={Boolean(config.tracking)}
                  trackingLabel={(config.tracking?.label || 'operación').toLowerCase()}
                  onChange={(next) => set({ tools: tools.map((item, i) => (i === openIndex ? next : item)) })}
                />
              </div>
              {footer(
                <button type="button" className="su-btn ghost" onClick={() => openAgentTab(tab)}>
                  Volver al agente
                </button>
              )}
            </>
          ) : (
            <>
              {panelHeader(LuSparkles, '#7563D8', 'Agente IA', agentTitle, dockButton)}
              <div className="su-ag-tabs" role="tablist" aria-label="Configuración del agente">
                {AGENT_TABS.map((item) => (
                  <button key={item.key} type="button" role="tab" aria-selected={item.key === tab} className={item.key === tab ? 'on' : ''} onClick={() => openAgentTab(item.key)}>
                    {item.label}
                  </button>
                ))}
              </div>
              <div className={`su-ag-body ${SCROLL_CLASS}${tab === 'instructions' ? ' flush' : ''}`}>
                <div className="su-ag-valid">
                  <ValidationBox validation={validation} />
                </div>
                {tab === 'instructions' && <InstructionsTab config={config} setInstructions={setInstructions} />}
                {tab === 'inputs' && <SettingsTab config={config} set={set} nodes={nodes} parts={['context', 'inputs']} />}
                {tab === 'tracking' && <SettingsTab config={config} set={set} nodes={nodes} parts={['tracking']} />}
                {tab === 'settings' && <SettingsTab config={config} set={set} nodes={nodes} parts={['general']} />}
              </div>
              {footer(null)}
            </>
          )}
        </aside>
      </div>
    </NodeFormProvider>
  );
};

export default AgentWorkspace;
